import React, { useState, useEffect, useContext } from 'react';
import { toast } from 'react-toastify';
import {
  getFolderDocuments,
  deleteDocument,
  downloadDocument,
  viewDocument,
} from '../../../../services/api';
import ThemeContext from '../../../../ThemeContext';
import AddDocumentModal from './modals/AddDocumentModal';
import AddPlaceholderModal from './modals/AddPlaceholderModal';
import RenameDocumentModal from './modals/RenameDocumentModal';
import DocumentViewerModal from './modals/DocumentViewerModal';
import MoveModal from './modals/MoveModal';
import CopyModal from './modals/CopyModal';
import ConfirmDeleteModal from './modals/ConfirmDeleteModal';

const DocumentsPanel = ({ selectedFolder, caseId, username, onRefresh }) => {
  const { theme } = useContext(ThemeContext);
  const [documents, setDocuments] = useState([]);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sortBy, setSortBy] = useState('custom');
  const [showSortMenu, setShowSortMenu] = useState(false);

  const [showAddDocumentModal, setShowAddDocumentModal] = useState(false);
  const [showAddPlaceholderModal, setShowAddPlaceholderModal] = useState(false);
  const [showRenameDocumentModal, setShowRenameDocumentModal] = useState(false);
  const [showDocumentViewerModal, setShowDocumentViewerModal] = useState(false);
  const [showMoveDocumentModal, setShowMoveDocumentModal] = useState(false);
  const [showCopyDocumentModal, setShowCopyDocumentModal] = useState(false);
  const [showDeleteDocumentModal, setShowDeleteDocumentModal] = useState(false);
  const [documentToEdit, setDocumentToEdit] = useState(null);
  const [documentToView, setDocumentToView] = useState(null);

  useEffect(() => {
    if (selectedFolder) {
      fetchDocuments();
    } else {
      setDocuments([]);
      setSelectedDocuments([]);
    }
  }, [selectedFolder]);

  const user = JSON.parse(localStorage.getItem("user"));
const userRole = user?.role;
const isClientUser = userRole === "client_user";

  const fetchDocuments = async () => {
    if (!selectedFolder) return;

    try {
      setIsLoading(true);
      const response = await getFolderDocuments(selectedFolder.id);
      setDocuments(response.data || []);
      setSelectedDocuments([]);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
      toast.error(`Failed to fetch documents: ${error.response?.data?.message || error.message}`, {
        style: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.danger,
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getSortedDocuments = () => {
    const sorted = [...documents];
    switch (sortBy) {
      case 'name':
        return sorted.sort((a, b) => a.name.localeCompare(b.name));
      case 'date':
        return sorted.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      case 'custom':
      default:
        return sorted;
    }
  };

  const handleSelectAll = () => {
    if (selectedDocuments.length === documents.length) {
      setSelectedDocuments([]);
    } else {
      setSelectedDocuments(documents.map((doc) => doc.id));
    }
  };

  const handleDocumentCheckbox = (documentId, event) => {
    event.stopPropagation();
    setSelectedDocuments((prev) => {
      if (prev.includes(documentId)) {
        return prev.filter((id) => id !== documentId);
      }
      return [...prev, documentId];
    });
  };

  const handleRowClick = (documentId, event) => {
    if (event.target.tagName === 'BUTTON' || event.target.tagName === 'INPUT') {
      return;
    }

    setSelectedDocuments((prev) => {
      if (prev.includes(documentId)) {
        return prev.filter((id) => id !== documentId);
      }
      return [...prev, documentId];
    });
  };

  const getFileExtension = (document) => {
    return (
      document?.file_type ||
      document?.extension ||
      document?.name?.split('.').pop() ||
      ''
    ).toLowerCase();
  };

  const handleViewDocument = (document, event) => {
  event.stopPropagation();

  const ext = getFileExtension(document);

  const supportedPreviewExtensions = [
    'pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp',
    'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx',
  ];

  if (document.is_placeholder || supportedPreviewExtensions.includes(ext)) {
    setDocumentToView(document);
    setShowDocumentViewerModal(true);
    return;
  }

  toast.warning('Preview is not available for this file type.', {
    style: {
      backgroundColor: theme.colors.bgPrimary,
      border: `1px solid ${theme.colors.borderColor}`,
      color: theme.colors.warning,
    },
  });
};

  const handleRenameDocument = (document, event) => {
    event.stopPropagation();
    setDocumentToEdit(document);
    setShowRenameDocumentModal(true);
  };

  const handleMoveDocument = () => {
    if (selectedDocuments.length === 0) {
      toast.warning('Please select documents to move', {
        style: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.warning,
        },
      });
      return;
    }
    setShowMoveDocumentModal(true);
  };

  const handleCopyDocument = () => {
    if (selectedDocuments.length === 0) {
      toast.warning('Please select documents to copy', {
        style: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.warning,
        },
      });
      return;
    }
    setShowCopyDocumentModal(true);
  };

  const handleDeleteDocument = () => {
    if (selectedDocuments.length === 0) {
      toast.warning('Please select documents to delete', {
        style: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.warning,
        },
      });
      return;
    }
    setShowDeleteDocumentModal(true);
  };

  const confirmDeleteDocuments = async () => {
    try {
      setIsLoading(true);
      for (const documentId of selectedDocuments) {
        await deleteDocument(documentId);
      }

      toast.success('Documents deleted successfully', {
        style: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.success,
        },
      });

      setSelectedDocuments([]);
      setShowDeleteDocumentModal(false);
      await fetchDocuments();
    } catch (error) {
      toast.error(`Failed to delete documents: ${error.response?.data?.message || error.message}`, {
        style: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.danger,
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadDocument = async (document, event) => {
    event.stopPropagation();

    try {
      const response = await downloadDocument(document.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = window.document.createElement('a');
      link.href = url;
      link.setAttribute('download', document.name);
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Document downloaded successfully', {
        style: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.success,
        },
      });
    } catch (error) {
      toast.error(`Failed to download document: ${error.response?.data?.message || error.message}`, {
        style: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.danger,
        },
      });
    }
  };

  const handleDownloadSelected = async () => {
    if (selectedDocuments.length === 0) {
      toast.warning('Please select documents to download', {
        style: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.warning,
        },
      });
      return;
    }

    for (const docId of selectedDocuments) {
      const document = documents.find((d) => d.id === docId);
      if (document && !document.is_placeholder) {
        await handleDownloadDocument(document, { stopPropagation: () => {} });
      }
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const sortedDocuments = getSortedDocuments();

  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        border: `1px solid ${theme.colors.borderColor}`,
        borderRadius: '8px',
        backgroundColor: theme.colors.bgSecondary,
        minHeight: '400px',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: 'clamp(0.5rem, 2vw, 0.75rem)',
          borderBottom: `1px solid ${theme.colors.borderColor}`,
          backgroundColor: theme.colors.bgPrimary,
          borderTopLeftRadius: '8px',
          borderTopRightRadius: '8px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <i
            className="bi bi-file-earmark-text-fill"
            style={{ color: theme.colors.primary, fontSize: 'clamp(12px, 2.5vw, 14px)' }}
          />
          <span
            style={{
              fontWeight: 'bold',
              fontSize: 'clamp(10px, 2.5vw, 11px)',
              color: theme.colors.textPrimary,
            }}
          >
            Documents ({documents.length})
          </span>

          <div style={{ position: 'relative', marginLeft: '1rem' }}>
            <button
              onClick={() => setShowSortMenu(!showSortMenu)}
              style={{
                background: theme.colors.bgSecondary,
                color: theme.colors.textPrimary,
                border: `1px solid ${theme.colors.borderColor}`,
                padding: 'clamp(4px, 1.5vw, 6px) clamp(8px, 2vw, 10px)',
                borderRadius: '5px',
                fontSize: 'clamp(9px, 2vw, 10px)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem',
              }}
            >
              <i
                className={`bi bi-${
                  sortBy === 'custom' ? 'list-check' : sortBy === 'date' ? 'calendar' : 'sort-alpha-down'
                }`}
              />
              {sortBy === 'custom' ? 'Custom' : sortBy === 'date' ? 'Date' : 'Name'}
              <i className="bi bi-chevron-down" />
            </button>

            {showSortMenu && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  marginTop: '0.25rem',
                  backgroundColor: theme.colors.bgPrimary,
                  border: `1px solid ${theme.colors.borderColor}`,
                  borderRadius: '5px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  zIndex: 1000,
                  minWidth: '120px',
                }}
              >
                {['custom', 'date', 'name'].map((option) => (
                  <button
                    key={option}
                    onClick={() => {
                      setSortBy(option);
                      setShowSortMenu(false);
                    }}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      fontSize: 'clamp(9px, 2vw, 10px)',
                      border: 'none',
                      background: sortBy === option ? theme.colors.bgSecondary : 'transparent',
                      color: theme.colors.textPrimary,
                      textAlign: 'left',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                    }}
                  >
                    {sortBy === option && (
                      <i className="bi bi-check" style={{ color: theme.colors.success }} />
                    )}
                    <span style={{ marginLeft: sortBy === option ? 0 : '1.25rem' }}>
                      {option.charAt(0).toUpperCase() + option.slice(1)}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
  {!isClientUser && (
    <>
      <button
        onClick={() => setShowAddDocumentModal(true)}
        disabled={!selectedFolder}
        style={{
          background: !selectedFolder ? theme.colors.bgSecondary : theme.gradients.primary,
          color: !selectedFolder ? theme.colors.textSecondary : theme.colors.textWhite,
          border: 'none',
          padding: 'clamp(4px, 1.5vw, 6px) clamp(8px, 2vw, 10px)',
          borderRadius: '5px',
          fontSize: 'clamp(9px, 2vw, 10px)',
          cursor: !selectedFolder ? 'not-allowed' : 'pointer',
          opacity: !selectedFolder ? 0.5 : 1,
        }}
      >
        Add Documents
      </button>

      <button
        onClick={() => setShowAddPlaceholderModal(true)}
        disabled={!selectedFolder}
        style={{
          background: !selectedFolder ? theme.colors.bgSecondary : theme.gradients.secondary,
          color: !selectedFolder ? theme.colors.textSecondary : theme.colors.textWhite,
          border: 'none',
          padding: 'clamp(4px, 1.5vw, 6px) clamp(8px, 2vw, 10px)',
          borderRadius: '5px',
          fontSize: 'clamp(9px, 2vw, 10px)',
          cursor: !selectedFolder ? 'not-allowed' : 'pointer',
          opacity: !selectedFolder ? 0.5 : 1,
        }}
      >
        Add Placeholder
      </button>
    </>
  )}
</div>
</div>
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: 'clamp(0.5rem, 2vw, 0.75rem)',
          maxHeight: 'calc(8 * 50px)',
        }}
      >
        {!selectedFolder ? (
          <div
            style={{
              textAlign: 'center',
              padding: '2rem',
              color: theme.colors.textSecondary,
              fontSize: 'clamp(9px, 2vw, 10px)',
            }}
          >
            Please select a folder to view documents
          </div>
        ) : isLoading ? (
          <div
            style={{
              textAlign: 'center',
              padding: '2rem',
              color: theme.colors.textSecondary,
              fontSize: 'clamp(9px, 2vw, 10px)',
            }}
          >
            Loading documents...
          </div>
        ) : documents.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: '2rem',
              color: theme.colors.textSecondary,
              fontSize: 'clamp(9px, 2vw, 10px)',
            }}
          >
            No documents in this folder
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'clamp(9px, 2vw, 10px)' }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${theme.colors.borderColor}` }}>
                <th style={{ padding: '0.5rem', textAlign: 'left', width: '30px' }}>
                  <input
  type="checkbox"
  checked={
    selectedDocuments.length > 0 &&
    selectedDocuments.length === documents.length
  }
  onChange={handleSelectAll}
  disabled={isClientUser}
/>
                </th>
                <th style={{ padding: '0.5rem', textAlign: 'left', color: theme.colors.textPrimary }}>
                  Select All
                </th>
                <th style={{ padding: '0.5rem', textAlign: 'left', color: theme.colors.textPrimary }}>
                  Size
                </th>
                <th style={{ padding: '0.5rem', textAlign: 'left', color: theme.colors.textPrimary }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedDocuments.map((document) => (
                <tr
                  key={document.id}
                  onClick={(event) => handleRowClick(document.id, event)}
                  style={{
                    borderBottom: `1px solid ${theme.colors.borderColor}`,
                    backgroundColor: selectedDocuments.includes(document.id)
                      ? theme.colors.bgSecondary
                      : theme.colors.bgPrimary,
                    cursor: 'pointer',
                  }}
                >
                  <td style={{ padding: '0.5rem' }}>
                    <input
  type="checkbox"
  checked={selectedDocuments.includes(document.id)}
  onChange={(event) => handleDocumentCheckbox(document.id, event)}
  disabled={isClientUser}
  style={{ cursor: isClientUser ? 'not-allowed' : 'pointer' }}
/>
                  </td>

                  <td style={{ padding: '0.5rem', color: theme.colors.textPrimary }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <i
                        className={document.is_placeholder ? 'bi bi-file-earmark-plus' : 'bi bi-file-earmark-pdf'}
                        style={{ color: document.is_placeholder ? theme.colors.warning : theme.colors.danger }}
                      />
                      <span>{document.name}</span>
                    </div>
                  </td>

                  <td style={{ padding: '0.5rem', color: theme.colors.textPrimary }}>
                    {document.is_placeholder ? 'Placeholder' : formatFileSize(document.file_size)}
                  </td>

                  <td style={{ padding: '0.5rem' }}>
                    <div style={{ display: 'flex', gap: '0.75rem' }}>
                      <button
                        onClick={(event) => handleViewDocument(document, event)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: theme.colors.primary,
                          fontSize: 'clamp(8px, 2vw, 9px)',
                          cursor: 'pointer',
                          textDecoration: 'underline',
                          padding: '0',
                        }}
                      >
                        View
                      </button>
                       {!isClientUser && (
                      <button
                        onClick={(event) => handleRenameDocument(document, event)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: theme.colors.primary,
                          fontSize: 'clamp(8px, 2vw, 9px)',
                          cursor: 'pointer',
                          textDecoration: 'underline',
                          padding: '0',
                        }}
                      >
                        Rename
                      </button>
                      )}

                      {!document.is_placeholder && (
                        <button
                          onClick={(event) => handleDownloadDocument(document, event)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: theme.colors.success,
                            fontSize: 'clamp(8px, 2vw, 9px)',
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            padding: '0',
                          }}
                        >
                          Download
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div
        style={{
          display: 'flex',
          gap: '0.5rem',
          padding: 'clamp(0.5rem, 2vw, 0.75rem)',
          borderTop: `1px solid ${theme.colors.borderColor}`,
          backgroundColor: theme.colors.bgSecondary,
          borderBottomLeftRadius: '8px',
          borderBottomRightRadius: '8px',
        }}
      >
        <span
          style={{
            fontSize: 'clamp(8px, 2vw, 9px)',
            color: theme.colors.textSecondary,
            alignSelf: 'center',
          }}
        >
          Selected ({selectedDocuments.length})
        </span>

        <button
          onClick={handleCopyDocument}
          disabled={selectedDocuments.length === 0}
          style={{
            flex: 1,
            padding: 'clamp(4px, 1.5vw, 6px)',
            fontSize: 'clamp(8px, 2vw, 9px)',
            border: 'none',
            borderRadius: '4px',
            cursor: selectedDocuments.length === 0 ? 'not-allowed' : 'pointer',
            backgroundColor: selectedDocuments.length === 0 ? theme.colors.bgSecondary : theme.colors.info,
            color: selectedDocuments.length === 0 ? theme.colors.textSecondary : theme.colors.textWhite,
            opacity: selectedDocuments.length === 0 ? 0.5 : 1,
          }}
        >
          Copy
        </button>

        <button
          onClick={handleDownloadSelected}
          disabled={selectedDocuments.length === 0}
          style={{
            flex: 1,
            padding: 'clamp(4px, 1.5vw, 6px)',
            fontSize: 'clamp(8px, 2vw, 9px)',
            border: 'none',
            borderRadius: '4px',
            cursor: selectedDocuments.length === 0 ? 'not-allowed' : 'pointer',
            backgroundColor: selectedDocuments.length === 0 ? theme.colors.bgSecondary : theme.colors.success,
            color: selectedDocuments.length === 0 ? theme.colors.textSecondary : theme.colors.textWhite,
            opacity: selectedDocuments.length === 0 ? 0.5 : 1,
          }}
        >
          Download
        </button>

        <button
          onClick={handleMoveDocument}
          disabled={selectedDocuments.length === 0}
          style={{
            flex: 1,
            padding: 'clamp(4px, 1.5vw, 6px)',
            fontSize: 'clamp(8px, 2vw, 9px)',
            border: 'none',
            borderRadius: '4px',
            cursor: selectedDocuments.length === 0 ? 'not-allowed' : 'pointer',
            backgroundColor: selectedDocuments.length === 0 ? theme.colors.bgSecondary : theme.colors.primary,
            color: selectedDocuments.length === 0 ? theme.colors.textSecondary : theme.colors.textWhite,
            opacity: selectedDocuments.length === 0 ? 0.5 : 1,
          }}
        >
          Move
        </button>

        <button
          onClick={handleDeleteDocument}
          disabled={selectedDocuments.length === 0}
          style={{
            flex: 1,
            padding: 'clamp(4px, 1.5vw, 6px)',
            fontSize: 'clamp(8px, 2vw, 9px)',
            border: 'none',
            borderRadius: '4px',
            cursor: selectedDocuments.length === 0 ? 'not-allowed' : 'pointer',
            backgroundColor: selectedDocuments.length === 0 ? theme.colors.bgSecondary : theme.colors.danger,
            color: selectedDocuments.length === 0 ? theme.colors.textSecondary : theme.colors.textWhite,
            opacity: selectedDocuments.length === 0 ? 0.5 : 1,
          }}
        >
          Delete
        </button>
      </div>

      <AddDocumentModal
        show={showAddDocumentModal}
        onHide={() => setShowAddDocumentModal(false)}
        folderId={selectedFolder?.id}
        onSuccess={fetchDocuments}
      />

      <AddPlaceholderModal
        show={showAddPlaceholderModal}
        onHide={() => setShowAddPlaceholderModal(false)}
        folderId={selectedFolder?.id}
        caseId={caseId}
        onSuccess={fetchDocuments}
      />

      <RenameDocumentModal
        show={showRenameDocumentModal}
        onHide={() => {
          setShowRenameDocumentModal(false);
          setDocumentToEdit(null);
        }}
        document={documentToEdit}
        onSuccess={fetchDocuments}
      />

      <DocumentViewerModal
        show={showDocumentViewerModal}
        onHide={() => {
          setShowDocumentViewerModal(false);
          setDocumentToView(null);
        }}
        document={documentToView}
      />

      <MoveModal
        show={showMoveDocumentModal}
        onHide={() => setShowMoveDocumentModal(false)}
        items={selectedDocuments}
        itemType="document"
        currentCaseId={caseId}
        onSuccess={() => {
          setSelectedDocuments([]);
          fetchDocuments();
        }}
      />

      <CopyModal
        show={showCopyDocumentModal}
        onHide={() => setShowCopyDocumentModal(false)}
        items={selectedDocuments}
        itemType="document"
        currentCaseId={caseId}
        onSuccess={() => {
          setSelectedDocuments([]);
          fetchDocuments();
        }}
      />

      <ConfirmDeleteModal
        show={showDeleteDocumentModal}
        onHide={() => setShowDeleteDocumentModal(false)}
        onConfirm={confirmDeleteDocuments}
        itemCount={selectedDocuments.length}
        itemType="document"
      />
    </div>
  );
};

export default DocumentsPanel;